import { action, makeObservable, observable, runInAction } from "mobx";
import { api } from "@/modules/api";
import { getBackendApiErrorMessage } from "@/modules/api/errorHandling";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { toastModule } from "@/modules/toast";
import { AppStore } from "@/store";
import { ChromeExtensionIntegrationOrganizingInstructions, IChromeExtensionStore } from "./types";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { asyncDataLoaded, asyncDataLoading } from "@/domains/async/AsyncData";
import { clientEnvModule } from "@/modules/client-env";
import {
  ChatModel,
  chatModelToWebClip,
  webClipToChatModel,
  type ApiWebClipModelKind,
} from "@/domains/chat-model";

export class AppStoreChromeExtensionStore implements IChromeExtensionStore {
  isInstalled = asyncDataLoading<boolean>({});
  isLoading = false;
  isSaving = false;
  isSavingModelSelection = false;
  organizingInstructions: ChromeExtensionIntegrationOrganizingInstructions = null;
  organizingInstructionsEnabled: boolean = false;
  selectedModelKind: ChatModel = ChatModel.Auto;

  constructor(private readonly store: AppStore) {
    makeObservable<AppStoreChromeExtensionStore>(this, {
      // Page lifecycle
      isInstalled: observable,
      useIsChromeExtensionInstalledUpdater: false,

      // Settings
      organizingInstructionsEnabled: observable,
      organizingInstructions: observable,
      selectedModelKind: observable,

      // Loading / Saving
      load: action,
      isLoading: observable,
      save: action,
      isSaving: observable,
      saveModelSelection: action,
      isSavingModelSelection: observable,
    });
  }

  load = async () => {
    runInAction(() => {
      this.isLoading = true;
    });

    try {
      const response = await api.get("/v2/integrations/web-clips/settings", {});
      const data = response.data;

      runInAction(() => {
        this.organizingInstructionsEnabled = data?.instructions_enabled ?? false;
        this.organizingInstructions = data?.instructions_markdown_content ?? null;
        this.selectedModelKind = webClipToChatModel(data?.selected_model_kind);
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "Failed to load web clip settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  save = async ({
    organizingInstructions,
    organizingInstructionsEnabled,
  }: {
    organizingInstructions: ChromeExtensionIntegrationOrganizingInstructions;
    organizingInstructionsEnabled: boolean;
  }) => {
    try {
      runInAction(() => {
        this.isSaving = true;
      });

      const enabled = organizingInstructionsEnabled ?? false;
      const instructions = organizingInstructions ?? null;

      const response = await api.post("/v2/integrations/web-clips/settings", {
        body: {
          instructions_enabled: enabled,
          instructions_markdown_content: instructions,
          selected_model_kind: chatModelToWebClip(this.selectedModelKind) as ApiWebClipModelKind,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        throw new Error(
          getBackendApiErrorMessage(response.error) ?? "Failed to save web clip settings"
        );
      }

      runInAction(() => {
        // Save altogther to avoid ui flickering.
        this.isSaving = false;
        this.organizingInstructionsEnabled = enabled;
        this.organizingInstructions = instructions;
      });
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreChromeExtensionStore] Failed to save web clip settings",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: "Could not save organizing instructions",
      });
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  saveModelSelection = async (modelKind: ChatModel) => {
    const previousModelKind = this.selectedModelKind;

    try {
      runInAction(() => {
        this.isSavingModelSelection = true;
        this.selectedModelKind = modelKind;
      });

      const response = await api.patch("/v2/integrations/web-clips/settings", {
        body: {
          selected_model_kind: chatModelToWebClip(modelKind) as ApiWebClipModelKind,
        },
      });

      const data = response.data;
      if (!data || response.error) {
        throw new Error(
          getBackendApiErrorMessage(response.error) ?? "Failed to save model selection"
        );
      }
    } catch (unknownError) {
      const error = unknownError as Error;
      logger.error({
        message: "[AppStoreChromeExtensionStore] Failed to save model selection",
        info: { error: objectModule.safeErrorAsJson(error) },
      });

      toastModule.triggerToast({
        content: "Could not save model selection",
      });

      runInAction(() => {
        this.selectedModelKind = previousModelKind;
      });
    } finally {
      runInAction(() => {
        this.isSavingModelSelection = false;
      });
    }
  };

  useIsChromeExtensionInstalledUpdater = () => {
    useEffectOnMount(() => {
      if (clientEnvModule.isDesktop()) {
        runInAction(() => {
          this.isInstalled = asyncDataLoaded(this.isInstalled, false);
        });
        return;
      }

      const check = () => {
        const isInstalled = document.querySelector("#mem-ext-shadow-host") !== null;
        runInAction(() => {
          this.isInstalled = asyncDataLoaded(this.isInstalled, isInstalled);
        });
        return isInstalled;
      };

      if (check()) return;

      const interval = setInterval(() => {
        if (check()) {
          clearInterval(interval);
        }
      }, 2000);

      return () => {
        clearInterval(interval);
      };
    });
  };
}
